import { useEffect, useRef, useState } from "react";
import LibraryBookDetail from "../components/LibraryBookDetail";
import { fetchCoLoan, fetchLibDetail } from "../api/coloan";
import { isbn13 } from "../api/books";

/**
 * 책 화면의 '함께 빌린 책' 탭.
 *
 * 전국 도서관에서 이 책을 빌린 사람이 함께 빌린 책이다. 모양은 바로 옆
 * '작가의 다른 책' 탭과 같게 둔다(author-book-card) — 같은 줄의 탭인데
 * 카드가 다르면 새로 붙인 티가 난다.
 * 카드를 누르면 그 책의 소개(소개글·출판사·연령별 대출)가 목록 아래에
 * 펼쳐진다. 무루에 있는 책이면 거기서 '무루에서 이 책 보기' 로 넘어간다 —
 * 예전처럼 누르자마자 넘어가면 어떤 책인지 보기도 전에 화면이 바뀐다.
 */
const norm = (t) => String(t ?? "").replace(/[([][^)\]]*[)\]]/g, "").replace(/\s*\d+\s*$/, "").replace(/\s+/g, "").toLowerCase();

export default function BookCoLoan({ book, allBooks = [], onBookClick }) {
  const isbn = isbn13(book?.isbn);
  const [state, setState] = useState({ loading: true, books: [], failed: false });
  const [sel, setSel] = useState(null);
  /** isbn → { loading } | { failed } | 받은 소개 */
  const [details, setDetails] = useState({});
  const detailRef = useRef(null);
  const pick = (isbn) => {
    setSel((cur) => (cur === isbn ? null : isbn));
    /* 소개는 누를 때만 받는다 — 정보나루 하루 한도(500건)를 아낀다 */
    if (!details[isbn] || details[isbn].failed) {
      setDetails((d) => ({ ...d, [isbn]: { loading: true } }));
      fetchLibDetail(isbn)
        .then((v) => setDetails((d) => ({ ...d, [isbn]: v })))
        .catch(() => setDetails((d) => ({ ...d, [isbn]: { failed: true } })));
    }
    setTimeout(() => detailRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 60);
  };

  useEffect(() => {
    let alive = true;
    setState({ loading: true, books: [], failed: false });
    setSel(null);
    fetchCoLoan({ isbn, title: book?.title })
      .then((books) => { if (alive) setState({ loading: false, books, failed: false }); })
      .catch(() => { if (alive) setState({ loading: false, books: [], failed: true }); });
    return () => { alive = false; };
  }, [isbn, book?.title]);

  const inApp = (b) => allBooks.find((x) => isbn13(x.isbn) === b.isbn)
    || allBooks.find((x) => norm(x.title) === norm(b.title));

  return (
    <div className="author-books-wrap">
      <div className="section-label" style={{ marginTop: "1rem" }}>이 책을 빌린 사람들이 함께 빌린 책</div>
      <div className="coloan-src">전국 도서관 대출 기록 · 도서관 정보나루</div>
      {state.loading ? (
        <div className="memo-empty">도서관 기록을 찾는 중...</div>
      ) : state.failed ? (
        <div className="memo-empty">지금은 도서관 기록을 불러오지 못했어요</div>
      ) : state.books.length === 0 ? (
        /* 전문서·수험서는 대출 기록이 없어 비어 온다(20권 중 3권이 그랬다) */
        <div className="memo-empty">이 책은 아직 도서관 대출 기록이 없어요</div>
      ) : (
        <div className="author-books-grid">
          {state.books.map((b, i) => {
            const app = inApp(b);
            return (
              <div
                key={b.isbn}
                className={`author-book-card${sel === b.isbn ? " coloan-on" : ""}`}
                style={{ animationDelay: `${i * 0.06}s` }}
                onClick={() => pick(b.isbn)}
                role="button"
                aria-expanded={sel === b.isbn}
              >
                <div className="author-book-cover">
                  {(b.cover || app?.cover) ? <img src={b.cover || app.cover} alt={b.title} loading="lazy" /> : <span>📖</span>}
                </div>
                <div className="author-book-info">
                  <div className="author-book-title">{b.title}</div>
                  <div className="author-book-year">{b.author}</div>
                  {app
                    ? <div className="author-book-badge in-app">앱에 등록됨</div>
                    : <div className="author-book-badge external">도서관</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 고른 책의 소개. 격자 사이에 끼우면 뒤 카드들이 밀려 자리가 바뀌어,
          목록 바로 아래에 둔다. */}
      {(() => {
        const b = state.books.find((x) => x.isbn === sel);
        if (!b) return null;
        const app = inApp(b);
        const det = details[b.isbn] || { loading: true };
        return (
          <div className="coloan-detail" ref={detailRef}>
            <div className="coloan-detail-title">『{b.title}』 <span>{b.author}</span></div>
            {det.loading ? (
              <p className="hb-desc hb-desc-none">소개를 불러오는 중...</p>
            ) : det.failed ? (
              <p className="hb-desc hb-desc-none">지금은 소개를 불러오지 못했어요. 잠시 뒤 다시 눌러 주세요</p>
            ) : null}
            <LibraryBookDetail book={det.loading || det.failed ? { ...b, desc: undefined } : { ...b, ...det }} hideEmpty={det.loading || det.failed}>
              {app && onBookClick && (
                <button className="hb-go" onClick={() => onBookClick(app.id)}>무루에서 이 책 보기 →</button>
              )}
            </LibraryBookDetail>
          </div>
        );
      })()}
    </div>
  );
}
